import { useMemo } from 'react';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';
import { useFindingBasePath, type FindingRefInfo } from './findingRefLabels';
import { splitNumbers, strataLabel } from './descriptionValidation';
import { locationLabel } from './locationLabel';

/** Lowercased Befundnummer -> the route its chip links to. */
export type StrataRouteMap = ReadonlyMap<string, string>;

/** A family description that could be the target of a positional relation. */
interface StrataSibling {
    id: string;
    projectId: string;
    findingId: string;
    area: string;
    /** The Planum-/Profilnummer, whichever kind the field is about. */
    number: string;
}

interface StrataFinding {
    id: string;
    projectId: string;
    findingNumber: string;
}

/** Trimmed and lowercased — how a Befundnummer is keyed here. */
function normalizeNumber(number: string): string {
    return number.trim().toLowerCase();
}

/**
 * Where the positional stratigraphy chips link to. Those fields store the BARE
 * Befundnummer (the ExcaBook wire format), so the target is resolved the same
 * way the option label is: the sibling description on the same Flaeche and
 * Planum-/Profilnummer. A number without one — free-typed, or the Flaeche has
 * changed since — falls back to the Befund's own editor, and a number the
 * family does not know stays unlinked.
 */
export function useStrataRouteMap({
    siblings,
    findings,
    area,
    number,
    kind
}: {
    /** The family's sibling descriptions of the field's kind. */
    siblings: readonly StrataSibling[];
    findings: readonly StrataFinding[];
    /** The Flaeche of the description being edited — the siblings' slot. */
    area: string;
    /** Its Planum-/Profilnummer. */
    number: string;
    kind: 'planum' | 'profile';
}): StrataRouteMap {
    const findingBase = useFindingBasePath();
    return useMemo(() => {
        const routes = new Map<string, string>();
        // The Befund's own editor first, so a description route overwrites it.
        for (const finding of findings) {
            const key = normalizeNumber(finding.findingNumber);
            if (!key || routes.has(key)) {
                continue;
            }
            routes.set(
                key,
                `${findingBase(finding.projectId, finding.id)}/edit`
            );
        }
        const numberByFindingId = new Map(
            findings.map((finding) => [finding.id, finding.findingNumber])
        );
        const segment = kind === 'planum' ? 'planums' : 'profiles';
        const described = new Set<string>();
        for (const sibling of siblings) {
            if (
                normalizeNumber(sibling.area) !== normalizeNumber(area) ||
                normalizeNumber(sibling.number) !== normalizeNumber(number)
            ) {
                continue;
            }
            const key = normalizeNumber(
                numberByFindingId.get(sibling.findingId) ?? ''
            );
            // First one wins, like the option list: a Befund described in Teilen
            // offers one chip, and it opens the first of them.
            if (!key || described.has(key)) {
                continue;
            }
            described.add(key);
            routes.set(
                key,
                `${findingBase(sibling.projectId, sibling.findingId)}/${segment}/${sibling.id}/edit`
            );
        }
        return routes;
    }, [siblings, findings, area, number, kind, findingBase]);
}

/** The one description a stratigraphy grid cell belongs to. */
interface StrataDescription {
    kind: 'planum' | 'profile';
    area: string;
    /** Its Planum-/Profilnummer. */
    number: string;
}

/** Key of a description slot: kind, Fläche, number and the Befund it holds. */
function slotKey(
    kind: 'planum' | 'profile',
    area: string,
    number: string,
    findingNumber: string
): string {
    return [kind, area, number, findingNumber].map(normalizeNumber).join('|');
}

function findingKey(findingNumber: string): string {
    return `finding|${normalizeNumber(findingNumber)}`;
}

/**
 * The stratigraphy fields of a whole grid, as the chips of a
 * FindingRefsCell: the same resolution the form's chips follow (see
 * useStrataRouteMap), only for every row at once instead of the one
 * description being edited.
 *
 * `resolver` holds a label and route per slot — "Bef. 7001, Fl. 1, Pl. 1" for
 * the description on the same Fläche and Planum-/Profilnummer — and per Befund,
 * "Bef. 7001", for a number with no such description. `refsOf` turns a row's
 * stored Befundnummern into keys of it; a number the family does not know
 * comes back as itself, which the cell prints as an unlinked chip.
 */
export function useStrataRefResolver(
    findings: readonly FindingDocType[],
    planums: readonly FindingPlanumDocType[],
    profiles: readonly FindingProfileDocType[]
): {
    resolver: Map<string, FindingRefInfo>;
    refsOf: (description: StrataDescription, stored: string) => string[];
} {
    const findingBase = useFindingBasePath();
    return useMemo(() => {
        const resolver = new Map<string, FindingRefInfo>();
        const numberByFindingId = new Map<string, string>();
        for (const finding of findings) {
            numberByFindingId.set(finding.id, finding.findingNumber);
            const key = findingKey(finding.findingNumber);
            // First one wins, as in the form.
            if (!normalizeNumber(finding.findingNumber) || resolver.has(key)) {
                continue;
            }
            resolver.set(key, {
                kind: 'finding',
                label: locationLabel({ findingNumber: finding.findingNumber }),
                to: `${findingBase(finding.projectId, finding.id)}/edit`
            });
        }
        const addSlot = (
            kind: 'planum' | 'profile',
            description: FindingPlanumDocType | FindingProfileDocType,
            number: string
        ) => {
            const findingNumber = numberByFindingId.get(description.findingId);
            if (!findingNumber) {
                return;
            }
            const key = slotKey(kind, description.area, number, findingNumber);
            // A Befund described in Teilen names one Lage: the first Teil's.
            if (resolver.has(key)) {
                return;
            }
            const segment = kind === 'planum' ? 'planums' : 'profiles';
            resolver.set(key, {
                kind,
                label: strataLabel(
                    findingNumber,
                    kind === 'planum' ? 'Planum' : 'Profil',
                    description.area,
                    number
                ),
                to: `${findingBase(description.projectId, description.findingId)}/${segment}/${description.id}/edit`
            });
        };
        for (const planum of planums) {
            addSlot('planum', planum, planum.planumNumber);
        }
        for (const profile of profiles) {
            addSlot('profile', profile, profile.profileNumber);
        }

        const refsOf = (description: StrataDescription, stored: string) =>
            splitNumbers(stored).map((findingNumber) => {
                const slot = slotKey(
                    description.kind,
                    description.area,
                    description.number,
                    findingNumber
                );
                if (resolver.has(slot)) {
                    return slot;
                }
                const befund = findingKey(findingNumber);
                return resolver.has(befund) ? befund : findingNumber;
            });

        return { resolver, refsOf };
    }, [findings, planums, profiles, findingBase]);
}
